import {
  Component,
  OnInit,
  ViewEncapsulation,
  HostBinding,
  ContentChild,
  ElementRef,
  Input,
  ChangeDetectionStrategy,
  HostListener,
  ChangeDetectorRef,
  NgZone,
  AfterContentInit,
  ViewChild,
  Inject,
} from '@angular/core';
import { PgFieldControl } from './pg-field-control';

import { startWith } from 'rxjs/operators';
import { PgLabelDirective } from '../pg-content-blocks/pg-label/pg-label.directive';
import {
  PG_FIELD_OPTIONS_TOKEN,
  PgFieldOptions,
  PgFieldSize,
} from './pg-field-options';

/**
  Field States:
    Idle/Untouched - The user has not yet interacted with the field and there is no data prepoulated in it.
    Hovered - the user's mouse is above the field
    Focused/Pressed - the user is focused on the field
    Touched - the user has focuesd and unfocused from the field at some point
    Error - the user has added incorrect data to the field and the field has been Touched.
    Complete - the user has added correct data to the field and the field is unfocused.
    Disabled - the user is unable to interact with the field..
    Disabled + Filled - the field has data in it and the user is unable to change or interact with the data.
*/

let nextUniqueId = 0;
const floatingLabelScale = 0.75;
const outlineGapPadding = 5;

/**
 * Boilerplate for applying mixins to MatFormField.
 * @docs-private
 */
export class PgFormFieldBase {
  constructor(public _elementRef: ElementRef) {}
}

@Component({
  selector: 'pg-field, grit-field',
  templateUrl: './pg-field.component.html',
  styleUrls: ['./pg-field.component.scss'],
  exportAs: 'pgField, gritField',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PgFieldComponent extends PgFormFieldBase
  implements AfterContentInit, OnInit {
  protected _uid = `pg-field + ${nextUniqueId++}`;
  @HostBinding('class')
  readonly _classList = 'pg-field grit';

  @HostBinding('class.is-error')
  get _classInvalid() {
    return this.control.isError;
  }

  @HostBinding('class.is-focused')
  get _classIsFocused() {
    return this.control.isFocused;
  }

  @HostBinding('class.is-complete')
  get _classIsComplete() {
    return (
      !this.control.isFocused &&
      !this._isHovered &&
      !this.control.isEmpty &&
      !this.control.isError
    );
  }

  @HostBinding('class.is-hovered')
  get _classIsHovered() {
    return this._isHovered;
  }
  protected _isHovered = false;

  @HostBinding('class.should-label-float')
  get _classShouldLabelFloat() {
    return this.control.shouldLabelFloat;
  }

  @HostBinding('class.is-disabled')
  get _classDisabled() {
    return this.control.isDisabled && this.control.isEmpty;
  }

  @HostBinding('class.is-disabled-filled')
  get _classIsDisabledFilled() {
    return this.control.isDisabled && !this.control.isEmpty;
  }

  @HostBinding('class.sm')
  get _classSm() {
    return this.size === 'sm';
  }

  @HostBinding('class.md')
  get _classMd() {
    return this.size === 'md' || !this.size;
  }

  @Input()
  size: PgFieldSize;

  @ViewChild('fieldBorder', { static: true })
  _fieldBorderChild: ElementRef;

  get fieldBorderEl(): HTMLElement {
    return this._fieldBorderChild.nativeElement;
  }

  /**
   * The implementation of PgFieldControl passed in as content
   * to this field class.
   */
  @ContentChild(PgFieldControl, { static: false })
  control: PgFieldControl<any>;

  @ContentChild(PgLabelDirective, { static: false })
  label: PgLabelDirective;

  constructor(
    public elementRef: ElementRef,
    private _cdRef: ChangeDetectorRef,
    @Inject(PG_FIELD_OPTIONS_TOKEN) private _options: PgFieldOptions
  ) {
    super(elementRef);
  }

  ngOnInit() {
    if (this.size === undefined || this.size === null) {
      this.size = this._options.fieldSize;
    }
  }
  @HostListener('mouseover')
  onMouseOver() {
    // TODO: make sure we don't need to do change detection here.
    this._isHovered = true;
  }

  @HostListener('mouseout')
  onMouseOut() {
    this._isHovered = false;
  }

  ngAfterContentInit() {
    this._validateControlChild();

    if (this.control.controlType) {
      this._elementRef.nativeElement.classList.add(
        `pg-form-field-type-${this.control.controlType}`
      );
    }

    // Subscribe to changes in the child control state in order to update the form field UI.
    this.control.stateChanges.pipe(startWith(null)).subscribe(() => {
      // this._validatePlaceholders();
      // this._syncDescribedByIds();
      this._cdRef.markForCheck();
    });

    if (this.label && this.control.id) {
      this.label.labelFor = this.control.id;
    }
  }

  /** Throws an error if the form field's control is missing. */
  protected _validateControlChild() {
    if (!this.control) {
      // throw getPgFormFieldMissingControlError();
      console.warn(`Field not a part of form group`);
    }
  }
}
